/* The Forma tab — canon `docs/design/analiza-wideo-2026-09-23/`, FINAL v4 and v6. The stage card
 * and the change table wear `DetailTab.module.css`, the Pomiary tab's own classes, because they are
 * the same card; what is here is only what Forma adds: the columns, the exercise list, the session
 * table, the panel and its rail, the recorded points, the five-week matrix and the gallery. */

/* The list keeps its open-panel width in every view and panel state, so its chip never moves. */
.workspace {
  --forma-list-track: clamp(
    var(--w-forma-list-min),
    calc(100% - var(--w-forma-centre-min) - var(--w-forma-dock-min) - 2 * var(--space-20px)),
    var(--w-forma-list)
  );

  display: grid;
  grid-template-columns:
    var(--forma-list-track) minmax(var(--w-forma-centre-min), 1fr)
    minmax(var(--w-forma-dock-min), var(--w-forma-dock));
  gap: var(--space-20px);
  align-items: start;
}

/* The tab's scroll box, as a size container: the sticky list is capped to the height the trainer
 * can see (`--h-forma-list-max` is `100cqb`), so its last row is reached by scrolling the list, not
 * by scrolling the page to its end. The box is a flex item sized by the screen, never by its
 * content, so size containment changes nothing about its own height. */
.scroller {
  container: forma-tab / size;
}

/* The gallery has no panel; the rail is the panel folded to its miniature. */
.workspaceGallery {
  grid-template-columns: var(--forma-list-track) minmax(0, 1fr);
}

.workspaceMatrix {
  grid-template-columns: minmax(0, 1fr);
}

.workspaceRail {
  grid-template-columns: var(--forma-list-track) minmax(var(--w-forma-centre-min), 1fr) var(--w-forma-rail);
}

/* The centre and the panel as one box until the panel's width is known. */
.columnsReserve {
  display: flex;
  flex-direction: column;
  grid-column: 2 / -1;
  gap: var(--space-3);
  min-width: 0;
}

.centre {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  container: forma-centre / inline-size;
}

.centreMatrix {
  gap: var(--space-4);
}

.eyebrow {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.unreadDot {
  flex: none;
  width: var(--space-7px);
  height: var(--space-7px);
  background: var(--accent);
  border-radius: var(--r-circle);
}

.list {
  position: sticky;
  top: var(--space-0);
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
  max-height: var(--h-forma-list-max);
  padding: var(--space-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

/* The head that stands while the rows scroll: the title with its two filters, then the search. */
.listTop {
  display: flex;
  flex: none;
  flex-direction: column;
}

.listHead {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
  padding: var(--space-2) var(--space-10px);
}

.listTitle {
  margin: var(--space-0);
  padding: var(--space-0);
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text);
}

/* The library toolbar's filter square, at the „Z nagraniem" chip's height beside it. Doubled so
 * the size outranks `filterSquare`'s whichever order the two modules land in. */
.listFilter.listFilter {
  width: var(--h-forma-filter-chip);
  height: var(--h-forma-filter-chip);
  margin-inline-start: auto;
}

/* `flex: none`, doubled over the compact field's `flex: 1`: in this COLUMN a zero basis is a height,
 * and it squashed the 27px field to its text. */
.listSearch.listSearch {
  flex: none;
  margin: var(--space-0) var(--space-10px) var(--space-2);
}

/* The popup layer, as `HintPopover` and the library's own panel sit on it. */
.listFilterPositioner {
  z-index: 40;
}

/* The library's filter panel draws its own card; this box only gives it the list's width and a
 * scroll when the window is shorter than the panel. */
.listFilterPopup {
  width: var(--anchor-width);
  max-height: var(--available-height);
  overflow-y: auto;
  outline: none;
}

.listEmpty {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-4) var(--space-2);
}

/* A toggle that reads pressed when it is on: „Z nagraniem" and „Przypnij". Geometry below. */
.pressChip {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  font: inherit;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
}

.filterChip {
  height: var(--h-forma-filter-chip);
  padding: var(--space-0) var(--space-10px) var(--space-0) var(--space-2);
  border-radius: var(--r-pill);
}

.pinButton {
  height: var(--sz-forma-panel-key);
  padding: var(--space-0) var(--space-10px);
  border-color: var(--border);
  border-radius: var(--radius-8px);
}

.pressChipOn {
  color: var(--text);
  background: var(--bg-chip);
  border-color: var(--border-3);
}

.filterChipGlyph {
  width: var(--space-14px);
  height: var(--space-14px);
}

/* „‹ Wszystkie nagrania": back to the whole gallery, only while one movement narrows it. */
.listBack {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  width: 100%;
  padding: var(--space-9px) var(--space-10px);
  font: inherit;
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
}

.listBack:hover {
  background: var(--bg-inset);
}

.listBackGlyph {
  width: var(--space-14px);
  height: var(--space-14px);
  color: var(--text-2);
}

/* The head and the back row stand; the rows scroll under them. A focused row is brought into view
 * by the browser, and its ring is drawn inset (`--outline-inset`), so this box clips none of it. */
.listBody {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.listRows {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.listRow,
.listRowWait {
  display: grid;
  grid-template-columns: var(--w-forma-thumb) minmax(0, 1fr);
  gap: var(--space-10px);
  align-items: center;
  width: 100%;
  padding: var(--space-10px);
  font: inherit;
  color: var(--text);
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
}

.listRow {
  cursor: pointer;
}

.listRow:hover {
  background: var(--bg-inset);
}

.listRow:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

.listRowOn,
.listRowOn:hover {
  background: var(--bg-chip);
}

.listThumb {
  display: block;
  width: var(--w-forma-thumb);
  height: var(--h-forma-thumb);
  overflow: hidden;
  background: var(--bg-inset);
  border-radius: var(--radius-8px);
}

.listThumbImg {
  display: block;
  width: 100%;
  height: 100%;
}

.listText {
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
  min-width: 0;
}

.listName {
  display: flex;
  gap: var(--space-7px);
  align-items: center;
  min-width: 0;
}

.listNameText {
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-name);
}

.listGlyph {
  display: inline-flex;
  flex: none;
  color: var(--text-2);
}

.listGlyph svg {
  width: var(--sz-forma-glyph);
  height: var(--sz-forma-glyph);
}

.listTrained {
  font-size: var(--fs-label);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.listNotice {
  margin: var(--space-0);
  padding: var(--space-10px);
  font-size: var(--fs-note);
  line-height: var(--lh-note);
  color: var(--text-2);
}

.head {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3) var(--space-4);
  align-items: flex-end;
}

.headCover {
  display: block;
  flex: none;
  width: var(--sz-forma-cover);
  height: var(--sz-forma-cover);
  overflow: hidden;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.headCoverImg {
  display: block;
  width: 100%;
  height: 100%;
}

.headText {
  flex: 1;
  min-width: 0;
}

/* One line, whatever the movement is called: the name lands before the stage does, and a second
 * line would push the stage down under the trainer. The list beside it carries the whole name.
 * The ellipsis clips at the padding box, and at 1.05 that box is 0.1em shorter than the glyphs each
 * side: „Przysiad bułgarski" lost the foot of its ą and g. The padding widens the clip to the glyph
 * box and the equal negative margin gives it back, so the heading's outer height is unchanged. */
.headTitle {
  min-height: calc(var(--fs-account-code) * var(--lh-heading));
  padding: var(--space-forma-title-bleed) var(--space-0);
  margin: var(--space-minus-forma-title-bleed) var(--space-0);
  overflow: hidden;
  font-size: var(--fs-account-code);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  letter-spacing: var(--ls-display);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.headSub {
  min-height: 1lh;
  margin: var(--space-6px) var(--space-0) var(--space-0);
  font-size: var(--fs-label-md);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.viewSwitch {
  margin-inline-start: auto;
}

/* A narrow centre gives the name its row and puts the switch under it, by width alone. */
@container forma-centre (width < 640px) {
  .headText {
    flex-basis: calc(100% - var(--sz-forma-cover) - var(--space-4));
  }
}

/* Beside the open panel the sentence takes two lines, held from the start. */
@container forma-centre (width < 800px) {
  .headSub {
    min-height: 2lh;
  }
}

.stageSay {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  justify-content: flex-start;
  min-height: var(--h-forma-say);
}

/* The switch's own height held while it waits, since which lines it offers is part of the answer. */
.lineRow {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-height: calc(var(--h-two-dates-pill) + 2 * var(--space-3px));
  margin: var(--space-14px) var(--space-0) var(--space-2);
}

/* A movement read on one line names it where the switch would stand, in the chosen option's ink. */
.lineOnly {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
}

.plot {
  min-height: var(--h-chart-plot-forma);
}

.plotReserve {
  display: grid;
  place-items: center;
  min-height: var(--h-chart-plot-forma);
}

/* Four short figures: the table's figure and change columns are narrower than Pomiary's. */
.changeCard {
  --dv-figure: var(--w-forma-dv-figure);
  --dv-change: var(--w-forma-dv-change);
}

/* In a narrow centre the table's fixed columns come down to their longest words, so the bars'
 * track keeps room for the heading over it and for a percentage past a full bar. After
 * `.changeCard`, which a container query does not outrank. */
@container forma-centre (width < 640px) {
  .changeCard {
    --dv-label: var(--w-forma-dv-label-narrow);
    --dv-figure: var(--w-forma-dv-figure-narrow);
    --dv-change: var(--w-forma-dv-change-narrow);
  }
}

.recordLayer {
  position: absolute;
  inset: var(--space-0);
  pointer-events: none;
}

.recordPoint {
  position: absolute;
  z-index: 2;
  width: var(--sz-forma-record);
  height: var(--sz-forma-record);
  padding: var(--space-0);
  cursor: pointer;
  pointer-events: auto;
  background: var(--accent);
  border: var(--border-forma-record) solid var(--bg-card);
  border-radius: var(--r-circle);
  box-shadow: var(--ring-accent);
  transform: translate(-50%, -50%);
}

.recordPoint:hover,
.recordPoint:focus-visible {
  transform: translate(-50%, -50%) scale(1.2);
}

.recordPoint:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.recordTip {
  position: absolute;
  top: var(--space-6px);
  z-index: 5;
  display: flex;
  gap: var(--space-10px);
  width: var(--w-forma-tip);
  padding: var(--space-10px) var(--space-3);
  pointer-events: none;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
  box-shadow: var(--shadow-card);
}

.recordTipPoster {
  display: block;
  flex: none;
  width: var(--w-forma-tip-poster);
  height: var(--h-forma-tip-poster);
  background: var(--player-stage);
  border-radius: var(--radius-xs);
}

.recordTipText {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

.recordTipDate,
.recordTipNote,
.recordTipHint {
  font-size: var(--fs-micro);
  line-height: var(--lh-hint);
}

.recordTipDate {
  color: var(--text-2);
}

.recordTipValue {
  font-size: var(--fs-readout-figure);
  font-weight: var(--fw-bold);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.recordTipNote {
  color: var(--text-2);
}

.recordTipHint {
  font-weight: var(--fw-semibold);
  color: var(--accent-ink);
}

/* A session with more sets than the card holds scrolls inside it, under the app's own drawn
 * scrollbar. The bar sits under the last row, often below the fold, so the edge with more beyond
 * it is also shaded, in tertiary ink in both themes: the covers travel with the content (`local`)
 * and hide the shade once that end is in view, so a table that fits shows none. */
.sessionCard {
  --sb-size: var(--space-2);
  --sb-radius: var(--radius-hair);

  display: flex;
  flex-direction: column;
  padding: var(--space-3) var(--space-10px) var(--space-6px);
  overflow-x: auto;
  background-color: var(--bg-card);
  background-image:
    linear-gradient(var(--bg-card), var(--bg-card)),
    linear-gradient(var(--bg-card), var(--bg-card)),
    radial-gradient(farthest-side at 0 50%, var(--text-3), transparent),
    radial-gradient(farthest-side at 100% 50%, var(--text-3), transparent);
  background-repeat: no-repeat;
  background-attachment: local, local, scroll, scroll;
  background-position: left center, right center, left center, right center;
  background-size: var(--w-scroll-edge) 100%;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

/* Every row is its own grid, so every row declares the same floor: a set never gets narrower than
 * its „72,5 × 5", and the figures give up to their floor before anything scrolls. */
.sessionGrid {
  display: grid;
  grid-template-columns:
    var(--w-forma-date) repeat(var(--forma-sets), minmax(var(--w-forma-set), 1fr))
    repeat(3, minmax(var(--w-forma-figure-min), var(--w-forma-figure)));
  column-gap: var(--space-6px);
  align-items: center;
  min-width: calc(
    var(--w-forma-date) + var(--forma-sets) * var(--w-forma-set) + 3 * var(--w-forma-figure-min) +
      (var(--forma-sets) + 3) * var(--space-6px) + 2 * var(--space-2)
  );
  padding: var(--space-3px) var(--space-2);
}

/* Each group is as wide as its rows, so a wider table is a resize the overflow measure hears. */
.sessionGroup {
  width: fit-content;
  min-width: 100%;
}

.sessionHead {
  padding-bottom: var(--space-2);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.sessionRows {
  display: flex;
  flex-direction: column;
  min-height: var(--h-forma-session-wait);
}

.sessionRow {
  min-height: var(--h-detail-btn);
  border-radius: var(--radius-sm);
}

.sessionRowOn {
  background: var(--bg-chip);
}

.sessionCentre {
  text-align: center;
}

.sessionDate {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.setCell {
  display: flex;
  gap: var(--space-5px);
  align-items: center;
  justify-content: center;
  width: 100%;
  height: var(--h-forma-cell);
  padding: var(--space-0) var(--space-1);
  font: inherit;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  background: var(--bg-inset);
  border: 0;
  border-radius: var(--radius-8px);
  font-variant-numeric: tabular-nums;
}

.setCellFilmed {
  color: var(--text);
  cursor: pointer;
  box-shadow: var(--ring-fill-source);
}

.setCellFilmed:hover {
  background: var(--bg-chip);
}

.setCellFilmed:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.setCellGlyph {
  flex: none;
  width: var(--icon-xs);
  height: var(--icon-xs);
}

.sessionFigure {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* A head longer than its track („Łączny czas") reaches left over the blank track before it rather
 * than wrapping, so the head row is one line for every movement. */
.sessionHead .sessionFigure {
  justify-self: end;
  white-space: nowrap;
}

/* The last figure column is the session's headline figure: e1RM for a lift, pace for a run. */
.sessionFigureMinor {
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.sessionFigureMain {
  font-size: var(--fs-body);
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

.sessionDrop {
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.sessionDropLoss {
  color: var(--warn-ink);
}

.dock {
  position: sticky;
  top: var(--space-0);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.dockBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.dockCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-14px);
  margin: var(--space-0);
  padding: var(--space-4);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.dockWait {
  align-items: center;
  min-height: var(--h-forma-dock-poster);
}

.dockSetRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
}

.dockKgReps {
  font-size: var(--fs-stat);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-display);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.sessionChip {
  display: inline-flex;
  align-items: center;
  height: var(--h-forma-chip);
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  background: var(--bg-chip);
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  border-radius: var(--r-pill);
}

.sessionChipOff {
  background: transparent;
  border-style: dashed;
  border-color: var(--border-2);
}

.dockMeta {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  justify-content: space-between;
  margin-top: var(--offset-forma-meta);
}

.dockDate {
  font-size: var(--fs-note);
  color: var(--text-2);
}

.dockBy {
  --sz-avatar: var(--sz-forma-avatar-sm);

  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--fs-label);
  color: var(--text-2);
}

/* The newest page of the thread, newest first. */
.dockThread {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.dockComment {
  display: flex;
  gap: var(--space-10px);
  align-items: flex-start;
  padding-top: var(--space-3);
  border-top: var(--border-hairline) solid var(--border);
}

.dockCommentText {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

.dockCommentHead {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  font-size: var(--fs-button-sm);
  color: var(--text);
}

.dockCommentWhen {
  font-size: var(--fs-label);
  color: var(--text-2);
}

.dockCommentStamp {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.dockCommentBody {
  font-size: var(--fs-button-sm);
  line-height: var(--lh-note);
  color: var(--text);
  overflow-wrap: anywhere;
}

.askCard {
  gap: var(--space-3);
  padding: var(--space-5);
}

.askTitle {
  margin: var(--space-0);
  font-size: var(--fs-stat);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-display);
  letter-spacing: var(--ls-title);
  color: var(--text);
}

.angles {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
}

.angle {
  display: inline-flex;
  align-items: center;
  height: var(--h-player-chip);
  padding: var(--space-0) var(--space-11px);
  font: inherit;
  font-size: var(--fs-label-md);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-chip);
}

.angleOn {
  color: var(--text);
  border-color: var(--text);
}

.angle:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.askLine {
  margin: var(--space-0);
  padding: var(--space-11px) var(--space-3);
  font-size: var(--fs-button-sm);
  line-height: var(--lh-relaxed);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.askButton {
  gap: var(--space-2);
  height: var(--h-control-field);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  border-radius: var(--radius-sm);
}

.askGlyph {
  width: var(--icon-xs);
  height: var(--icon-xs);
}

.askSent {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  align-self: flex-start;
  height: var(--h-player-chip);
  padding: var(--space-0) var(--space-11px);
  font-size: var(--fs-label-md);
  color: var(--text);
  border: var(--border-hairline) solid var(--text);
  border-radius: var(--r-chip);
}

.askStatus {
  min-height: calc(var(--fs-label) * var(--lh-hint));
  margin: var(--space-0);
  font-size: var(--fs-label);
  line-height: var(--lh-hint);
  color: var(--danger-ink);
}

.matrixCard {
  display: flex;
  flex-direction: column;
  padding: var(--space-14px) var(--space-4) var(--space-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.matrixNote {
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-note);
  color: var(--text-2);
}

.matrixGrid {
  display: grid;
  grid-template-columns:
    minmax(0, var(--w-forma-matrix-name)) repeat(5, minmax(0, 1fr))
    var(--w-forma-matrix-change);
  column-gap: var(--space-2);
  align-items: center;
}

.matrixHead {
  padding-bottom: var(--space-2);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.matrixRows {
  display: flex;
  flex-direction: column;
}

.matrixRow {
  padding: var(--space-1) var(--space-0);
}

.matrixName {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-1);
  font: inherit;
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-xs);
}

.matrixName:hover {
  text-decoration: underline;
}

.matrixName:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.matrixCell {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  align-items: center;
  justify-content: center;
  height: var(--h-forma-matrix-cell);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text);
  background: var(--bg-inset);
  border-radius: var(--radius-sm);
  font-variant-numeric: tabular-nums;
}

.matrixCellEmpty {
  background: transparent;
  border: var(--border-hairline) dashed var(--border);
}

.matrixSub {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* The change over the name of what it is measured on: „+12 kg" over „e1RM". */
.matrixChange {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  align-items: flex-end;
  font-size: var(--fs-body);
  font-weight: var(--fw-extrabold);
  color: var(--text-2);
}

.matrixChangeGood {
  color: var(--good-ink);
}

.skelThumb {
  width: var(--w-forma-thumb);
  height: var(--h-forma-thumb);
}

.skelLine {
  width: 70%;
  height: var(--fs-note);
}

.skelTitle {
  display: inline-block;
  width: 60%;
  height: var(--fs-stat);
}

.skelDate {
  width: 70%;
  height: var(--fs-note);
}

.skelCell {
  height: var(--h-forma-cell);
}

.skelMatrixCell {
  height: var(--h-forma-matrix-cell);
}

.skelPoster {
  width: 60%;
  height: var(--h-forma-dock-poster);
}

.skelRailThumb {
  flex: none;
  width: var(--w-forma-rail-thumb);
  height: var(--h-forma-rail-thumb);

  --radius-bar: var(--radius-forma-rail-thumb);
}

.skelReserveHead {
  width: 40%;
  height: var(--sz-forma-cover);
}

.skelReserveStage {
  width: 100%;
  height: calc(var(--h-forma-say) + var(--h-chart-plot-forma));

  --radius-bar: var(--radius-md);
}

.skelReserveTable {
  width: 100%;
  height: var(--h-forma-session-wait);

  --radius-bar: var(--radius-md);
}

.skelLineShort {
  width: 45%;
  height: var(--fs-note);
}

/* The panel's head: „Przypnij" and the fold arrow. */
.dockHead {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  justify-content: space-between;
}

.pinGlyph,
.panelArrowGlyph {
  width: var(--space-14px);
  height: var(--space-14px);
}

.panelArrow {
  width: var(--sz-forma-panel-key);
  height: var(--sz-forma-panel-key);
  background: transparent;
  border-radius: var(--radius-8px);
}

/* „Nagranie z 15 wrz · Pokaż najnowsze" — an older recording stands in the panel. */
.dockWhich {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin: var(--space-0);
  font-size: var(--fs-label);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.dockWhichLink {
  --inline-text-ink: var(--text);

  text-decoration: underline;
  text-underline-offset: var(--space-3px);
}

/* The canon sets the link in the sentence's own weight; two classes outrank the shared bold. */
.dockWhich .dockWhichLink {
  font-weight: inherit;
}

/* The folded panel: a card as tall as the centre, 64px wide. */
.rail {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  align-items: center;
  align-self: stretch;
  width: var(--w-forma-rail);
  padding: var(--space-10px) var(--space-0);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.railBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  align-items: center;
}

.railThumb {
  position: relative;
  display: block;
  flex: none;
  width: var(--w-forma-rail-thumb);
  height: var(--h-forma-rail-thumb);
  padding: var(--space-0);
  overflow: hidden;
  cursor: pointer;
  background: var(--player-stage);
  background-clip: padding-box;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-forma-rail-thumb);
}

.railThumbImg {
  display: block;
  width: 100%;
  height: 100%;
}

.railPlay {
  position: absolute;
  inset: var(--space-0);
  display: grid;
  place-items: center;
  color: var(--text-on-photo);
  background: var(--wash-on-photo-soft);
}

.railPlayGlyph {
  width: var(--space-4);
  height: var(--space-4);
}

.railThumbNone {
  display: grid;
  place-items: center;
  color: var(--text-2);
  background: var(--bg-inset);
  border-style: dashed;
}

.railNoneGlyph {
  width: var(--space-14px);
  height: var(--space-14px);
}

.railText {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  transform: rotate(180deg);
  writing-mode: vertical-rl;
}

/* „Nagrania": months of tiles, newest first. */
.galleryCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-height: var(--h-forma-gallery-wait);
  padding: var(--space-14px) var(--space-20px) var(--space-22px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

.galleryBody {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
}

.galleryEmpty {
  display: grid;
  flex: 1;
  place-items: center;
}

.pressChip:focus-visible,
.listBack:focus-visible,
.railThumb:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}
